import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { ThreeHero } from '../components/ThreeHero';
import { Card3D } from '../components/Card3D';
import { PageTransition } from '../components/PageTransition';
const BRYAN_PHOTO = 'https://cdn.magicpatterns.com/uploads/jcswc8oANSg6paLTVfoQXv/IMG_0042.jpg';
export function HomePage() {
  return <PageTransition data-id="element-277">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-navy-900 overflow-hidden bg-glow" data-id="element-278">
        <ThreeHero data-id="element-279" />
        <div className="absolute inset-0 bg-navy-900/50 z-[1]" data-id="element-280" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-24" data-id="element-281">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20 items-center" data-id="element-282">
            {/* Photo - Left Column (2/5 width) */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="lg:col-span-2 flex justify-center lg:justify-end" data-id="element-283">
              <div className="relative" data-id="element-284">
                {/* Decorative gold corner accents */}
                <div className="absolute -top-2 -left-2 w-12 h-12 border-t-2 border-l-2 border-gold/60" data-id="element-285" />
                <div className="absolute -bottom-2 -right-2 w-12 h-12 border-b-2 border-r-2 border-gold/60" data-id="element-286" />
                <img src={BRYAN_PHOTO} alt="Bryan E. Nicholson, Director of Field Operations" className="w-64 md:w-72 lg:w-80 aspect-[3/4] object-cover object-top shadow-2xl" data-id="element-287" />
              </div>
            </motion.div>

            {/* Text Content - Right Column (3/5 width) */}
            <div className="lg:col-span-3 text-center lg:text-left" data-id="element-288">
              <motion.div initial={{
              opacity: 0,
              width: 0
            }} animate={{
              opacity: 1,
              width: '48px'
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="h-0.5 bg-gold mb-6 mx-auto lg:mx-0" data-id="element-289" />

              <motion.p initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="text-gold font-semibold tracking-[0.2em] text-xs uppercase mb-4" data-id="element-290">
                Midstream Operations Executive
              </motion.p>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.5
            }} className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-wide mb-3 leading-[1.1]" data-id="element-291">
                Bryan E. Nicholson
              </motion.h1>

              <motion.h2 initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.65
            }} className="text-lg md:text-xl text-white/60 font-medium mb-8" data-id="element-292">
                Director, Field Operations – Gas Processing
              </motion.h2>

              <motion.p initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.8
            }} className="text-base md:text-lg text-white/75 mb-10 leading-relaxed max-w-lg mx-auto lg:mx-0" data-id="element-293">
                20+ years leading cryogenic gas processing and midstream
                operations across Texas — driving operational excellence, safety
                performance, and long-term enterprise value.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.95
            }} className="flex flex-col sm:flex-row items-center lg:items-start gap-4" data-id="element-294">
                <Link to="/experience" className="px-7 py-3 border border-white/25 text-white/90 hover:bg-white hover:text-navy-900 transition-all duration-300 font-medium tracking-wide text-sm w-full sm:w-auto text-center" data-id="element-295">
                  View Experience
                </Link>
                <Link to="/contact" className="px-7 py-3 bg-gold text-navy-900 hover:bg-gold-light transition-all duration-300 font-semibold tracking-wide text-sm w-full sm:w-auto text-center" data-id="element-296">
                  Get in Touch
                </Link>
                <a href="https://heyzine.com/flip-book/2846c58bee.html" target="_blank" rel="noopener noreferrer" className="px-7 py-3 border border-gold/40 text-gold hover:bg-gold/10 transition-all duration-300 font-medium tracking-wide text-sm w-full sm:w-auto text-center inline-flex items-center justify-center gap-2" data-id="element-297">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-298">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-299" />
                    <polyline points="14 2 14 8 20 8" data-id="element-300" />
                    <line x1="16" y1="13" x2="8" y2="13" data-id="element-301" />
                    <line x1="16" y1="17" x2="8" y2="17" data-id="element-302" />
                    <polyline points="10 9 9 9 8 9" data-id="element-303" />
                  </svg>
                  View Resume
                </a>
                <a href="https://heyzine.com/flip-book/1f85c38c97.html" target="_blank" rel="noopener noreferrer" className="px-7 py-3 border border-white/25 text-white/90 hover:bg-white/10 transition-all duration-300 font-medium tracking-wide text-sm w-full sm:w-auto text-center inline-flex items-center justify-center gap-2" data-id="element-304">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-305">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" data-id="element-306" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" data-id="element-307" />
                  </svg>
                  Leadership Philosophy Statement
                </a>
                <a href="https://heyzine.com/flip-book/db91dfc67a.html" target="_blank" rel="noopener noreferrer" className="px-7 py-3 border border-white/25 text-white/90 hover:bg-white/10 transition-all duration-300 font-medium tracking-wide text-sm w-full sm:w-auto text-center inline-flex items-center justify-center gap-2" data-id="element-308">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-309">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-310" />
                    <circle cx="8.5" cy="7" r="4" data-id="element-311" />
                    <line x1="20" y1="8" x2="20" y2="14" data-id="element-312" />
                    <line x1="23" y1="11" x2="17" y2="11" data-id="element-313" />
                  </svg>
                  Executive Board Bio
                </a>
              </motion.div>
            </div>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 1.5
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce" data-id="element-314">
          <ChevronDownIcon className="text-white/40 w-7 h-7" data-id="element-315" />
        </motion.div>
      </section>

      {/* Stats Bar */}
      <section className="bg-navy-800 py-16" data-id="element-316">
        <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-317">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-318">
            <Card3D data-id="element-319">
              <div className="bg-navy-900 border border-white/10 p-8 text-center" data-id="element-320">
                <span className="text-4xl md:text-5xl font-bold text-gold block mb-2" data-id="element-321">
                  20+
                </span>
                <span className="text-white/70 text-sm font-medium" data-id="element-322">
                  Years Experience
                </span>
              </div>
            </Card3D>
            <Card3D data-id="element-323">
              <div className="bg-navy-900 border border-white/10 p-8 text-center" data-id="element-324">
                <span className="text-4xl md:text-5xl font-bold text-gold block mb-2" data-id="element-325">
                  720
                </span>
                <span className="text-white/70 text-sm font-medium" data-id="element-326">
                  MMSCFD Capacity
                </span>
              </div>
            </Card3D>
            <Card3D data-id="element-327">
              <div className="bg-navy-900 border border-white/10 p-8 text-center" data-id="element-328">
                <span className="text-4xl md:text-5xl font-bold text-gold block mb-2" data-id="element-329">
                  5
                </span>
                <span className="text-white/70 text-sm font-medium" data-id="element-330">
                  Processing Trains
                </span>
              </div>
            </Card3D>
            <Card3D data-id="element-331">
              <div className="bg-navy-900 border border-white/10 p-8 text-center" data-id="element-332">
                <span className="text-4xl md:text-5xl font-bold text-gold block mb-2" data-id="element-333">
                  200
                </span>
                <span className="text-white/70 text-sm font-medium" data-id="element-334">
                  MMSCFD Largest Plant
                </span>
              </div>
            </Card3D>
          </div>
        </div>
      </section>

      {/* Brief Intro */}
      <section className="bg-light py-20" data-id="element-335">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center" data-id="element-336">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-337">
            <p className="text-lg md:text-xl text-ink-muted leading-relaxed mb-8" data-id="element-338">
              A seasoned operations executive with deep expertise in cryogenic
              gas processing, fractionation, and midstream operations.
              Recognized as a trusted advisor to executive leadership and
              boards, providing strategic operational guidance and performance
              optimization.
            </p>
            <Link to="/about" className="inline-flex items-center text-gold font-semibold hover:text-gold-light transition-colors group" data-id="element-339">
              Learn More About Bryan
              <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-340" />
            </Link>
          </motion.div>
        </div>
      </section>
    </PageTransition>;
}